import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import {
  AUTH_TOKEN_KEY,
  CURRENCY_KEY as STORAGE_KEY,
  CURRENCY_SOURCE_KEY as STORAGE_SOURCE_KEY,
  EV_PROFILE_UPDATED,
  EV_CURRENCY_CHANGED,
} from "@/lib/constants";
import {
  BASE_CURRENCY,
  COUNTRY_TO_CURRENCY,
  countryFromBrowser,
  convertAmount,
  formatCurrency,
} from "@/lib/currencyUtils";

/**
 * Currency resolution precedence (per user spec — profile is canonical
 * for AUTHENTICATED users and MUST NEVER be overridden by detection or
 * a temporary USD fallback):
 *   AUTHENTICATED users:
 *     1. profile.preferred_currency            → source: "profile"
 *     2. profile.country_code (mapped)         → source: "profile"
 *     3. localStorage preference (last-known)  → source: "preference"
 *   ANONYMOUS users only:
 *     4. backend /api/currency/detect (CDN → IP → USD)
 *     5. browser locale country map
 *     6. USD                                   → source: "fallback"
 */

const CurrencyContext = createContext(null);

// --- helper hooks ---------------------------------------------------------

/** Loads /currency/rates + /currency/supported once. */
function useCurrencyCatalog() {
  const [rates, setRates] = useState({});
  const [ratesTs, setRatesTs] = useState(null);
  const [ratesStale, setRatesStale] = useState(false);
  const [supported, setSupported] = useState([]);

  useEffect(() => {
    api.get("/currency/supported").then((r) => setSupported(r.data.currencies || [])).catch(() => {});
    api.get("/currency/rates?base=USD").then((r) => {
      setRates(r.data.rates || {});
      setRatesTs(r.data.fetched_at);
      setRatesStale(!!r.data.stale);
    }).catch(() => {});
  }, []);

  return { rates, ratesTs, ratesStale, supported };
}

/** Poll AUTH_TOKEN_KEY changes (login/logout) and re-pull profile currency. */
function useAuthTokenWatcher(onTokenGained, onTokenLost) {
  useEffect(() => {
    let prev = localStorage.getItem(AUTH_TOKEN_KEY);
    const tick = () => {
      const curr = localStorage.getItem(AUTH_TOKEN_KEY);
      if (curr !== prev) {
        const was = prev;
        prev = curr;
        if (curr && !was) onTokenGained();
        if (!curr && was) onTokenLost();
      }
    };
    const id = setInterval(tick, 1000);
    window.addEventListener("storage", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("storage", tick);
    };
  }, [onTokenGained, onTokenLost]);
}

/** Listen for EV_PROFILE_UPDATED custom events and sync currency. */
function useProfileUpdateListener(applyFromProfile) {
  useEffect(() => {
    const onUpdate = async (ev) => {
      const prof = ev?.detail;
      if (prof && (prof.preferred_currency || prof.country_code)) {
        applyFromProfile(prof);
      } else {
        try {
          const { data } = await api.get("/profile");
          applyFromProfile(data);
        } catch (_) { /* ignore */ }
      }
    };
    window.addEventListener(EV_PROFILE_UPDATED, onUpdate);
    return () => window.removeEventListener(EV_PROFILE_UPDATED, onUpdate);
  }, [applyFromProfile]);
}

// --- provider -------------------------------------------------------------

export function CurrencyProvider({ children }) {
  // NOTE: we intentionally do NOT seed from localStorage on first render for
  // authenticated users — the seeded value could be stale (user changed their
  // profile currency on another device or the profile has been updated since).
  // We start unresolved and mark `ready=false` until the correct value is
  // established from /profile (auth'd) or /currency/detect (anonymous). This
  // is what prevents the "USD flash" for non-USD users.
  const hasToken = () => {
    try { return !!localStorage.getItem(AUTH_TOKEN_KEY); } catch { return false; }
  };

  const [currency, setCurrencyState] = useState(null);
  const [source, setSource] = useState(null);
  const [ready, setReady] = useState(false);
  const [detecting, setDetecting] = useState(true);
  const { rates, ratesTs, ratesStale, supported } = useCurrencyCatalog();

  const currencyRef = useRef(null);
  useEffect(() => { currencyRef.current = currency; }, [currency]);

  const applyCurrency = useCallback((code, src) => {
    const upper = String(code || BASE_CURRENCY).toUpperCase();
    const prev = currencyRef.current;
    try {
      localStorage.setItem(STORAGE_KEY, upper);
      localStorage.setItem(STORAGE_SOURCE_KEY, src);
    } catch (_) { /* private mode */ }
    setCurrencyState(upper);
    setSource(src);
    setReady(true);
    setDetecting(false);
    if (prev && prev !== upper) {
      try {
        window.dispatchEvent(new CustomEvent(EV_CURRENCY_CHANGED, {
          detail: { previous: prev, current: upper, source: src },
        }));
      } catch (_) { /* SSR */ }
    }
  }, []);

  const applyFromProfile = useCallback((prof) => {
    if (!prof) return false;
    if (prof.preferred_currency) {
      applyCurrency(String(prof.preferred_currency).toUpperCase(), "profile");
      return true;
    }
    if (prof.country_code && COUNTRY_TO_CURRENCY[prof.country_code]) {
      applyCurrency(COUNTRY_TO_CURRENCY[prof.country_code], "profile");
      return true;
    }
    return false;
  }, [applyCurrency]);

  // Keep the latest applyFromProfile referentially-stable for cross-event use.
  const applyFromProfileRef = useRef(applyFromProfile);
  useEffect(() => { applyFromProfileRef.current = applyFromProfile; }, [applyFromProfile]);
  const stableApplyFromProfile = useCallback(
    (prof) => applyFromProfileRef.current(prof),
    [],
  );

  // Full auth-aware detection cycle.
  const runDetection = useCallback(async () => {
    setDetecting(true);
    setReady(false);
    let cancelled = false;
    const finish = (code, src) => {
      if (cancelled) return;
      applyCurrency(code, src);
    };

    if (hasToken()) {
      // Authenticated: PROFILE is canonical.
      try {
        const { data: prof } = await api.get("/profile");
        if (cancelled) return;
        if (prof?.preferred_currency) {
          finish(String(prof.preferred_currency).toUpperCase(), "profile");
          return;
        }
        if (prof?.country_code && COUNTRY_TO_CURRENCY[prof.country_code]) {
          finish(COUNTRY_TO_CURRENCY[prof.country_code], "profile");
          return;
        }
        // Auth'd but incomplete profile → fall through to detection.
      } catch (_) {
        // /profile failed (stale token / offline). Fall through to detection
        // as if anonymous — the auth watcher will re-run once tokens change.
      }
    }

    // Anonymous OR authenticated-with-no-currency-clue → detect.
    try {
      const cached = (() => {
        try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
      })();
      if (cached && !hasToken()) {
        // Anonymous returning visitor: honor their last-known override.
        finish(cached, "preference");
        return;
      }
      const { data: det } = await api.get("/currency/detect");
      if (cancelled) return;
      if (det?.currency) { finish(det.currency, det.source || "ip"); return; }
    } catch (_) { /* detect failed */ }
    const cc = countryFromBrowser();
    if (cc && COUNTRY_TO_CURRENCY[cc]) {
      finish(COUNTRY_TO_CURRENCY[cc], "locale");
      return;
    }
    finish(BASE_CURRENCY, "fallback");

    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [applyCurrency]);

  // Initial resolution on mount.
  useEffect(() => {
    runDetection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-resolve on login/logout transitions.
  const onTokenGained = useCallback(() => { runDetection(); }, [runDetection]);
  const onTokenLost = useCallback(() => {
    // Clear override so anonymous view sees fresh detection.
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_SOURCE_KEY);
    } catch (_) { /* private mode */ }
    runDetection();
  }, [runDetection]);

  useAuthTokenWatcher(onTokenGained, onTokenLost);
  useProfileUpdateListener(stableApplyFromProfile);

  const setPreference = useCallback(async (code) => {
    const upper = String(code || BASE_CURRENCY).toUpperCase();
    applyCurrency(upper, "profile");
    try {
      await api.put("/profile", { preferred_currency: upper, currency_source: "preference" });
    } catch (_) { /* offline is fine — will retry on next PUT */ }
  }, [applyCurrency]);

  const resetAuto = useCallback(async () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_SOURCE_KEY);
    } catch (_) { /* private mode */ }
    try { await api.put("/profile", { preferred_currency: null, currency_source: "auto" }); } catch (_) { /* offline */ }
    await runDetection();
  }, [runDetection]);

  const refresh = useCallback(async () => {
    if (!hasToken()) return currencyRef.current;
    try {
      const { data: prof } = await api.get("/profile");
      if (applyFromProfile(prof)) return prof.preferred_currency || COUNTRY_TO_CURRENCY[prof.country_code];
    } catch (_) { /* ignore */ }
    return currencyRef.current;
  }, [applyFromProfile]);

  const convert = useCallback(
    (amount, from = BASE_CURRENCY) => convertAmount(amount, from, currency || BASE_CURRENCY, rates),
    [currency, rates],
  );

  const format = useCallback(
    (amount, opts = {}) => formatCurrency(amount, currency, opts),
    [currency],
  );

  const value = useMemo(() => ({
    currency: currency || BASE_CURRENCY,
    source,
    ready,
    detecting,
    rates,
    ratesTs,
    ratesStale,
    supported,
    baseCurrency: BASE_CURRENCY,
    convert,
    format,
    setPreference,
    resetAuto,
    refresh,
  }), [currency, source, ready, detecting, rates, ratesTs, ratesStale, supported, convert, format, setPreference, resetAuto, refresh]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}

/**
 * Subscribe to EV_CURRENCY_CHANGED. `handler` runs on every change but
 * NOT on the initial mount. Return the unsubscribe function.
 * Use this in components/pages that cache currency-scoped payloads
 * (salary report, roadmap, executive assessment, jobs matches) so they
 * re-fetch when the user switches currency.
 */
export function useCurrencyChangeListener(handler) {
  useEffect(() => {
    if (typeof handler !== "function") return undefined;
    const wrapped = (ev) => handler(ev?.detail || {});
    window.addEventListener(EV_CURRENCY_CHANGED, wrapped);
    return () => window.removeEventListener(EV_CURRENCY_CHANGED, wrapped);
  }, [handler]);
}
